import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import SimpleModal from "../../common/SimpleModal/SimpleModal";
import warningIcon from "../../resources/icons/warning-info.svg";
import CreateOrganisationModal from "../../common/CreateOrganisationModal/CreateOrganisationModal";
import EntityConstants from "../../configs/EntityConstants";
import CreateTenantUserModal from "../../common/CreateTenantUserModal/CreateTenantUserModal";
import CertificateTypeChooserModal, {
  CERTIFICATE_DESIGN_TYPE,
  CERTIFICATE_ONBOARDING_ROUTES,
} from "../../common/CertificateOnboarding/components/CertificateTypeChooserModal";
import { ChevronRight, Ban } from "lucide-react";
import { isHtmlDesignsBuildEnabled } from "../../hooks/useHtmlDesignsEnabled";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import SimpleToggleButton from "../../common/SimpleToggleButton/SimpleToggleButton";
import ToolTip from "../../common/ToolTip/ToolTip";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const AdminUserOrganisation = () => {
  const { tenant_code } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const [organisations, setOrganisations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgNext, setOrgNext] = useState(null);
  const [orgPrevious, setOrgPrevious] = useState(null);
  const [orgLimit] = useState(6);
  const [orgOffset, setOrgOffset] = useState(0);
  const [updatingOrgCode, setUpdatingOrgCode] = useState(null);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [isOrgSearching, setIsOrgSearching] = useState(false);
  const [orgToggleRefresh, setOrgToggleRefresh] = useState(false);
  const [hasActiveOrganisation, setHasActiveOrganisation] = useState(false);
  const [activeOrgLoading, setActiveOrgLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [userNext, setUserNext] = useState(null);
  const [userPrevious, setUserPrevious] = useState(null);
  const [userLimit] = useState(6);
  const [userOffset, setUserOffset] = useState(0);
  const [updatingUserEmail, setUpdatingUserEmail] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [userToggleRefresh, setUserToggleRefresh] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showCertificateTypeChooser, setShowCertificateTypeChooser] =
    useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    type: "",
    item: null,
    currentStatus: false,
  });

  const isTenantAdmin = authContext.userType?.toLowerCase() === "tenant_admin";
  const isTenantUser = authContext.userType?.toLowerCase() === "tenant_user";
  const isSuperAdmin = authContext.userType?.toLowerCase() === "super_admin";

  const hasTenantAdmin = users.some(
    (user) => user.user_type?.toLowerCase() === "tenant_admin",
  );

  const hasTenantUser = users.some(
    (user) => user.user_type?.toLowerCase() === "tenant_user",
  );

  const isTenantRole = isTenantAdmin || isTenantUser;
  const isCreateCertificateDisabled =
    isTenantRole && (orgLoading || activeOrgLoading || !hasActiveOrganisation);
  const createCertificateDisabledTooltip =
    isTenantRole && !orgLoading && !activeOrgLoading && !hasActiveOrganisation
      ? t("tenant:create_certificate_requires_organisation")
      : "";

  const performOrgSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${EntityConstants.GET_TENANT_ORGANISATIONS}?tenant_code=${tenant_code}&limit=${orgLimit}&offset=0&search=${searchTerm}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.success) {
        const results = Array.isArray(data.data?.results)
          ? data.data.results
          : [];
        setOrganisations(results);
        setOrgNext(data.data?.next ?? null);
        setOrgPrevious(data.data?.previous ?? null);
        setOrgOffset(0);
      } else {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: data.message || t("tenant:failed_search_organisations"),
        });
        setOrganisations([]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search organisations error:", error);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_search_organisations"),
        });
        setOrganisations([]);
      }
    }
  };

  const { handleSearch: handleOrgSearch } = useSearchDebounce(
    performOrgSearchAPI,
    500,
  );

  const performUserSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${EntityConstants.GET_TENANT_USERS}?tenant_code=${tenant_code}&limit=${userLimit}&offset=0&search=${searchTerm}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setUserNext(data.data.next);
        setUserPrevious(data.data.previous);
        setUserOffset(0);
      } else {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: data.message || t("tenant:failed_search_users"),
        });
        setUsers([]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search users error:", error);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_search_users"),
        });
        setUsers([]);
      }
    }
  };

  const { handleSearch: handleUserSearch } = useSearchDebounce(
    performUserSearchAPI,
    500,
  );

  const fetchOrganisations = async (
    offset = 0,
    search = "",
    isToggle = false,
  ) => {
    if (!isToggle) {
      setOrgLoading(true);
    }
    try {
      const endpoint = search
        ? `${EntityConstants.GET_TENANT_ORGANISATIONS}?tenant_code=${tenant_code}&limit=${orgLimit}&offset=${offset}&search=${search}`
        : `${EntityConstants.GET_TENANT_ORGANISATIONS}?tenant_code=${tenant_code}&limit=${orgLimit}&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.success) {
        const results = Array.isArray(data.data?.results)
          ? data.data.results
          : [];
        const next = data.data?.next ?? null;
        const previous = data.data?.previous ?? null;
        setOrganisations(results);
        setOrgNext(next);
        setOrgPrevious(previous);
        setOrgOffset(offset);
      } else {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: data.message || t("tenant:failed_fetch_organisations"),
        });
      }
    } catch (error) {
      console.error("Error fetching organisations:", error);
      Toast({
        type: "error",
        title: t("tenant:error"),
        message: t("tenant:failed_fetch_organisations"),
      });
    } finally {
      setOrgLoading(false);
      if (isToggle) {
        setOrgToggleRefresh(false);
      }
    }
  };

  const fetchHasActiveOrganisation = async () => {
    if (!tenant_code) return;

    setActiveOrgLoading(true);
    try {
      const params = new URLSearchParams({
        tenant_code,
        limit: "1",
        offset: "0",
        is_active: "true",
      });
      const response = await GET(
        `${EntityConstants.GET_TENANT_ORGANISATIONS}?${params.toString()}`,
      );
      const data = await response.json();
      const results =
        data.success && Array.isArray(data.data?.results)
          ? data.data.results
          : [];
      setHasActiveOrganisation(results.length > 0);
    } catch (error) {
      console.error("Failed to check active organisations", error);
      setHasActiveOrganisation(false);
    } finally {
      setActiveOrgLoading(false);
    }
  };

  const fetchUsers = async (offset = 0, search = "", isToggle = false) => {
    if (!isToggle) {
      setUserLoading(true);
    }
    try {
      const endpoint = search
        ? `${EntityConstants.GET_TENANT_USERS}?tenant_code=${tenant_code}&limit=${userLimit}&offset=${offset}&search=${search}`
        : `${EntityConstants.GET_TENANT_USERS}?tenant_code=${tenant_code}&limit=${userLimit}&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setUserNext(data.data.next);
        setUserPrevious(data.data.previous);
        setUserOffset(offset);
      } else {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: data.message || t("tenant:failed_fetch_users"),
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      Toast({
        type: "error",
        title: t("tenant:error"),
        message: t("tenant:failed_fetch_users"),
      });
    } finally {
      setUserLoading(false);
      if (isToggle) {
        setUserToggleRefresh(false);
      }
    }
  };

  useEffect(() => {
    if (tenant_code) {
      fetchOrganisations(0);
      fetchUsers(0);
      if (isTenantRole) {
        fetchHasActiveOrganisation();
      }
    }
  }, [tenant_code]);

  const handleOrgToggle = (org) => {
    setConfirmModal({
      show: true,
      type: "org",
      item: org,
      currentStatus: org.is_active,
    });
  };

  const handleOrgRowClick = (org) => {
    navigate(
      `/issuer/tenant/${org.tenant_code}/organisations/${org.org_code}/organisations`,
    );
  };

  const handleUserToggle = (user) => {
    setConfirmModal({
      show: true,
      type: "user",
      item: user,
      currentStatus: user.is_active,
    });
  };

  const confirmToggle = async () => {
    const { type, item, currentStatus } = confirmModal;

    if (!item) {
      cancelToggle();
      return;
    }

    if (type === "org") {
      setUpdatingOrgCode(item.org_code);
      try {
        const payload = {
          org_code: item.org_code,
          status: !currentStatus,
        };

        const response = await POST(
          EntityConstants.POST_TENANT_UPDATE_ORGANISATION,
          payload,
        );

        const data = await response.json();

        if (data.success) {
          Toast({
            type: "success",
            title: t("tenant:success"),
            message:
              data.message ||
              t("tenant:organisation_status_updated_successfully"),
          });
          setOrgToggleRefresh(true);
          fetchOrganisations(orgOffset, orgSearchTerm, true);
          if (isTenantRole) {
            fetchHasActiveOrganisation();
          }
        } else {
          Toast({
            type: "error",
            title: t("tenant:error"),
            message:
              data.message || t("tenant:failed_update_organisation_status"),
          });
        }
      } catch (error) {
        console.error("Error updating organisation:", error);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_update_organisation_status"),
        });
      } finally {
        setUpdatingOrgCode(null);
      }
    } else if (type === "user") {
      setUpdatingUserEmail(item.email);
      try {
        const payload = {
          email: item.email,
          status: !currentStatus,
          tenant_code: tenant_code,
        };

        const response = await POST(
          `${EntityConstants.POST_TENANT_UPDATE_USER}`,
          payload,
        );

        const data = await response.json();

        if (data.success) {
          Toast({
            type: "success",
            title: t("tenant:success"),
            message:
              data.message || t("tenant:user_status_updated_successfully"),
          });
          setUserToggleRefresh(true);
          fetchUsers(userOffset, userSearchTerm, true);
        } else {
          Toast({
            type: "error",
            title: t("tenant:error"),
            message: data.message || t("tenant:failed_update_user_status"),
          });
        }
      } catch (error) {
        console.error("Error updating user:", error);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_update_user_status"),
        });
      } finally {
        setUpdatingUserEmail(null);
      }
    }

    setConfirmModal({
      show: false,
      type: "",
      item: null,
      currentStatus: false,
    });
  };

  const cancelToggle = () => {
    setConfirmModal({
      show: false,
      type: "",
      item: null,
      currentStatus: false,
    });
  };

  const handleOrgPrevious = () => {
    if (orgPrevious !== null) {
      const prevOffset = Math.max(0, orgOffset - orgLimit);
      fetchOrganisations(prevOffset, orgSearchTerm);
    }
  };

  const handleOrgNext = () => {
    if (orgNext !== null) {
      const nextOffset = orgOffset + orgLimit;
      fetchOrganisations(nextOffset, orgSearchTerm);
    }
  };

  const handleUserPrevious = () => {
    if (userPrevious !== null) {
      const prevOffset = Math.max(0, userOffset - userLimit);
      fetchUsers(prevOffset, userSearchTerm);
    }
  };

  const handleUserNext = () => {
    if (userNext !== null) {
      const nextOffset = userOffset + userLimit;
      fetchUsers(nextOffset, userSearchTerm);
    }
  };

  const handleOrgCreated = () => {
    fetchOrganisations(orgOffset, orgSearchTerm);
    if (isTenantRole) {
      fetchHasActiveOrganisation();
    }
  };

  const handleUserCreated = () => {
    fetchUsers(userOffset, userSearchTerm);
  };

  const handleOrgSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setOrgSearchTerm(value);
    setOrgOffset(0);

    if (value.length > 0) {
      setIsOrgSearching(true);
      handleOrgSearch(value, {
        onStart: () => setIsOrgSearching(true),
        onComplete: () => setIsOrgSearching(false),
        onError: () => {
          setOrganisations([]);
          setIsOrgSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsOrgSearching(false);
      fetchOrganisations(0, "");
    }
  };

  const handleUserSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setUserSearchTerm(value);
    setUserOffset(0);

    if (value.length > 0) {
      setIsUserSearching(true);
      handleUserSearch(value, {
        onStart: () => setIsUserSearching(true),
        onComplete: () => setIsUserSearching(false),
        onError: () => {
          setUsers([]);
          setIsUserSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsUserSearching(false);
      fetchUsers(0, "");
    }
  };

  const handleTenantClick = () => {
    navigate("/issuer/tenant");
  };

  const OrganisationRow = ({ org, index }) => {
    return (
      <div
        key={index}
        className="flex flex-row items-center border rounded-[var(--cf-issued-credential-border-radius)] py-[var(--cf-issued-credential-template-card-padding-y)] px-6 hover:bg-[#F9FAFB] min-h-[5rem] min-w-[78.8rem] cursor-pointer"
        id={`admin-user-organisation-org-row-${org.org_code}`}
        onClick={(event) => {
          if (event.target.closest("[data-toggle-action]")) return;
          handleOrgRowClick(org);
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOrgRowClick(org);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="w-1/5 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText font-semibold whitespace-nowrap truncate">
            {org.org_name}
          </p>
        </div>
        <div className="w-1/5 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">{org.org_code}</p>
        </div>
        <div className="w-1/5 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">
            {org.org_type || t("tenant:na")}
          </p>
        </div>
        <div className="w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              org.is_active
                ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfIssuedCredentialTemplateCardStatusText"
                : "bg-cfIssuedCredentialTemplateCardStatusRevokedBG text-white"
            }`}
          >
            {org.is_active ? t("tenant:active") : t("tenant:inactive")}
          </span>
        </div>
        <div
          data-toggle-action
          className={`w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden flex justify-center items-center ${
            updatingOrgCode === org.org_code
              ? "cursor-not-allowed"
              : "cursor-default"
          }`}
        >
          <div className="relative inline-flex items-center">
            <SimpleToggleButton
              checked={org.is_active}
              onChange={() => handleOrgToggle(org)}
              disabled={updatingOrgCode === org.org_code}
              testId={`admin-user-organisation-toggle-org-${org.org_code}`}
              ariaLabel={t("tenant:toggle_status_for", {
                name: org.org_name,
              })}
            />
          </div>
        </div>
      </div>
    );
  };

  const UserRow = ({ user, index }) => {
    return (
      <div
        key={index}
        id={`admin-user-organisation-user-row-${user.email}`}
        className="flex flex-row items-center border rounded-[var(--cf-issued-credential-border-radius)] py-[var(--cf-issued-credential-template-card-padding-y)] px-6 hover:bg-[#F9FAFB] min-h-[5rem] min-w-[78.8rem]"
      >
        <div className="w-1/5 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText font-semibold whitespace-nowrap truncate">
            {user.user_name ||
              `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
              t("tenant:na")}
          </p>
        </div>
        <div className="w-1/5 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">{user.email}</p>
        </div>
        <div className="w-1/5 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">{user.user_type}</p>
        </div>
        <div className="w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              user.is_active
                ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfIssuedCredentialTemplateCardStatusText"
                : "bg-cfIssuedCredentialTemplateCardStatusRevokedBG text-white"
            }`}
          >
            {user.is_active ? t("tenant:active") : t("tenant:inactive")}
          </span>
        </div>
        <div
          className={`w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden flex justify-center items-center ${
            updatingUserEmail === user.email
              ? "cursor-not-allowed"
              : "cursor-default"
          }`}
        >
          <div className="relative inline-flex items-center">
            <SimpleToggleButton
              checked={user.is_active}
              onChange={() => handleUserToggle(user)}
              disabled={updatingUserEmail === user.email}
              testId={`admin-user-organisation-toggle-user-${user.email}`}
              ariaLabel={t("tenant:toggle_status_for", {
                name: user.email,
              })}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="flex flex-col items-center w-full pb-3 pt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.adminUserOrganisation}
    >
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[1.4rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
            <h2 className="text-lg font-medium flex items-center justify-center w-full">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                {!(isTenantAdmin || isTenantUser) ? (
                  <>
                    <button
                      onClick={handleTenantClick}
                      id="admin-user-organisation-breadcrumb-tenant"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleTenantClick();
                        }
                      }}
                      className="hover:text-gray-800 cursor-pointer focus:outline-none"
                    >
                      {t("tenant:tenant_management")}
                    </button>
                    <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                    <span className="text-gray-800 font-medium">
                      {t("tenant:admin_management")}
                    </span>
                  </>
                ) : (
                  <div className="text-cfHeadingText font-semibold">
                    {t("tenant:admin_management")}
                  </div>
                )}
              </div>
            </h2>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col mt-4 px-4">
        {!isSuperAdmin && (
          <div className="mb-8">
            <div className="flex flex-col md:flex-row sm:flex-row justify-between items-start sm:items-center mb-4">
              <h2 className="text-lg font-semibold text-cfHeadingText">
                {t("tenant:tenant_organisations")}
              </h2>
              <div className="flex flex-col md:flex-row items-start gap-3">
                {(isTenantAdmin || isTenantUser) && (
                  <ToolTip
                    testId="admin-user-organisation-create-certificate-tooltip"
                    component={
                      <span
                        className={
                          isCreateCertificateDisabled
                            ? "inline-flex cursor-not-allowed"
                            : "inline-flex"
                        }
                      >
                        <button
                          type="button"
                          onClick={() =>
                            isTenantAdmin && isHtmlDesignsBuildEnabled()
                              ? setShowCertificateTypeChooser(true)
                              : navigate(
                                  CERTIFICATE_ONBOARDING_ROUTES[
                                    CERTIFICATE_DESIGN_TYPE.SVG
                                  ],
                                )
                          }
                          id="admin-user-organisation-create-certificate"
                          disabled={isCreateCertificateDisabled}
                          className="px-4 py-2 bg-cfPrimaryButton text-white font-medium rounded-md transition-colors duration-200 hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-cfPrimaryButton"
                        >
                          {t("tenant:create_certificate")}
                        </button>
                      </span>
                    }
                    message={createCertificateDisabledTooltip}
                    tooltipPosition="top-[-4rem]"
                    bgColor="#374151"
                    tooltipWidth="max-w-[240px]"
                  />
                )}
                <button
                  onClick={() => setShowCreateOrgModal(true)}
                  id="admin-user-organisation-create-organisation"
                  className={`px-4 py-2 bg-cfPrimaryButton text-white font-medium rounded-md hover:bg-cfPrimaryDark transition-colors duration-200`}
                >
                  {t("tenant:create_organisation")}
                </button>

                <input
                  type="text"
                  name="search"
                  id="admin-user-organisation-input-org-search"
                  placeholder={t("tenant:search_organisations_name_code")}
                  className="outline-none border border-gray-300 px-2 py-[2rem] rounded-lg placeholder:text-sm"
                  style={{
                    width: "350px",
                    fontSize: "0.875rem",
                    lineHeight: "1.4rem",
                  }}
                  value={orgSearchTerm}
                  onChange={handleOrgSearchInputChange}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="flex flex-col justify-center gap-2 min-w-full">
                <div className="flex bg-cfTableHeaderBackgroundColor items-center min-h-[4.5rem] border text-center px-6 font-bold text-[14px] min-w-[78.8rem]">
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    {t("tenant:organisation_name")}
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    {t("tenant:code")}
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    {t("tenant:type")}
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    {t("tenant:status")}
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    {t("tenant:action")}
                  </div>
                </div>

                {isOrgSearching && !orgToggleRefresh ? (
                  <div className="flex justify-center items-center min-h-[8rem]">
                    <CfLoader />
                  </div>
                ) : orgLoading ? (
                  <div className="flex justify-center items-center min-h-[8rem]">
                    <CfLoader />
                  </div>
                ) : organisations.length === 0 ? (
                  <div
                    id="admin-user-organisation-no-orgs"
                    className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg"
                  >
                    {t("tenant:no_organisations_found")}
                  </div>
                ) : (
                  organisations.map((org) => (
                    <OrganisationRow key={org.org_code} org={org} />
                  ))
                )}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 w-full">
              <button
                onClick={handleOrgPrevious}
                id="admin-user-organisation-org-pagination-previous"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={orgPrevious === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>{t("tenant:previous")}</span>
                </span>
              </button>

              <button
                onClick={handleOrgNext}
                id="admin-user-organisation-org-pagination-next"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={orgNext === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>{t("tenant:next")}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        )}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <h2 className="text-lg font-semibold text-cfHeadingText">
              {isSuperAdmin
                ? t("tenant:tenant_admin")
                : t("tenant:tenant_users")}
            </h2>
            <div className="flex flex-col md:flex-row items-start gap-3">
              <div className="relative group">
                <div className="relative inline-block group">
                  <button
                    id="admin-user-organisation-create-user"
                    disabled={isSuperAdmin && (hasTenantAdmin || hasTenantUser)}
                    onClick={() => {
                      if (isSuperAdmin && (hasTenantAdmin || hasTenantUser))
                        return;
                      setShowCreateUserModal(true);
                    }}
                    className={`px-4 py-2 font-medium rounded-md transition-colors duration-200 ${
                      isSuperAdmin && (hasTenantAdmin || hasTenantUser)
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-cfPrimaryButton text-white hover:bg-cfPrimaryDark cursor-pointer"
                    }`}
                  >
                    {isSuperAdmin
                      ? t("tenant:create_tenant_admin")
                      : t("tenant:create_tenant_users")}
                  </button>
                  {isSuperAdmin && (hasTenantAdmin || hasTenantUser) && (
                    <span className="absolute -top-2 -right-2 hidden group-hover:block">
                      <Ban className="w-5 h-5 text-red-500" />
                    </span>
                  )}
                </div>

                {isSuperAdmin && (hasTenantAdmin || hasTenantUser) && (
                  <div
                    className="absolute z-10 hidden group-hover:block
                              top-full mt-2 left-1/2 -translate-x-1/2
                              bg-gray-800 text-white text-xs rounded-md px-3 py-1
                              whitespace-nowrap shadow-lg"
                  >
                    {t("tenant:only_one_tenant_admin")}
                  </div>
                )}
              </div>

              <input
                type="text"
                name="search"
                id="admin-user-organisation-input-user-search"
                placeholder={t("tenant:search_tenant_admins")}
                className="outline-none border border-gray-300 px-2 py-[2rem] rounded-lg placeholder:text-sm"
                style={{
                  width: "350px",
                  fontSize: "0.875rem",
                  lineHeight: "1.4rem",
                }}
                value={userSearchTerm}
                onChange={handleUserSearchInputChange}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="flex flex-col justify-center gap-2 min-w-full">
              <div className="flex bg-cfTableHeaderBackgroundColor items-center min-h-[4.5rem] border text-center min-w-[78.8rem] px-6 font-bold text-[14px]">
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  {t("tenant:name")}
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  {t("tenant:email")}
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  {t("tenant:user_type")}
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  {t("tenant:status")}
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  {t("tenant:action")}
                </div>
              </div>

              {isUserSearching && !userToggleRefresh ? (
                <div className="flex justify-center items-center min-h-[8rem]">
                  <CfLoader />
                </div>
              ) : userLoading ? (
                <div className="flex justify-center items-center min-h-[8rem]">
                  <CfLoader />
                </div>
              ) : users.length === 0 ? (
                <div
                  id="admin-user-organisation-no-users"
                  className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg"
                >
                  {t("tenant:no_users_found")}
                </div>
              ) : (
                users.map((user) => <UserRow key={user.email} user={user} />)
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 w-full">
            <button
              onClick={handleUserPrevious}
              id="admin-user-organisation-user-pagination-previous"
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
              disabled={userPrevious === null}
            >
              <span className="flex items-center justify-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                  />
                </svg>
                <span>{t("tenant:previous")}</span>
              </span>
            </button>

            <button
              onClick={handleUserNext}
              id="admin-user-organisation-user-pagination-next"
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
              disabled={userNext === null}
            >
              <span className="flex items-center justify-center gap-2">
                <span>{t("tenant:next")}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      <CreateOrganisationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        onOrganisationCreated={handleOrgCreated}
        tenantCode={tenant_code}
      />

      <CreateTenantUserModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onUserCreated={handleUserCreated}
      />

      {isTenantAdmin && (
        <CertificateTypeChooserModal
          isOpen={showCertificateTypeChooser}
          onClose={() => setShowCertificateTypeChooser(false)}
          onSelect={(type) => {
            setShowCertificateTypeChooser(false);
            navigate(CERTIFICATE_ONBOARDING_ROUTES[type]);
          }}
        />
      )}

      <SimpleModal
        isOpen={confirmModal.show}
        closeModal={cancelToggle}
        successFunction={confirmToggle}
        modalIcon={warningIcon}
        modalHeaderMessage={`${
          confirmModal.currentStatus
            ? t("tenant:deactivate")
            : t("tenant:activate")
        } ${
          confirmModal.type === "org"
            ? t("tenant:organisation")
            : t("tenant:user")
        }`}
        modalBodyMessage={`${t("tenant:are_you_sure_want_to")} ${
          confirmModal.currentStatus
            ? t("tenant:deactivate_text")
            : t("tenant:activate_text")
        } "${
          confirmModal.type === "org"
            ? confirmModal.item?.org_name
            : confirmModal.item?.first_name + " " + confirmModal.item?.last_name
        }"?`}
        leftButtonMessage={t("tenant:cancel")}
        rightButtonMessage={t("tenant:confirm")}
        testId="admin-user-organisation-confirm-modal"
      />
    </div>
  );
};

export default AdminUserOrganisation;
